import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { ExcavationSite, PredictedSite } from '../types';
import { PERIOD_COLORS } from '../constants';
import { Layers, Eye, EyeOff } from 'lucide-react';

interface ExcavationMapProps {
  sites: ExcavationSite[];
  predictedSites?: PredictedSite[];
  onSiteClick: (site: ExcavationSite | PredictedSite, isPredicted: boolean) => void;
  highlightedPeriod?: string | null;
  onPeriodSelect?: (period: string | null) => void;
}

export const ExcavationMap: React.FC<ExcavationMapProps> = ({ 
  sites, 
  predictedSites = [], 
  onSiteClick, 
  highlightedPeriod, 
  onPeriodSelect 
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const layersRef = useRef<L.LayerGroup | null>(null);
  
  // Layer visibility state
  const [showExisting, setShowExisting] = useState(true);
  const [showPredicted, setShowPredicted] = useState(true);

  // Calculate counts for badges based on current visibility/filters
  const existingCount = sites.filter(s => !highlightedPeriod || s.period === highlightedPeriod).length;
  const predictedCount = predictedSites.filter(s => !highlightedPeriod || (s.period && s.period === highlightedPeriod)).length;

  const getColorForPeriod = (period: string) => {
    const p = (period || '').toLowerCase();
    for (const [key, color] of Object.entries(PERIOD_COLORS)) {
      if (p.includes(key.toLowerCase())) return color;
    }
    return '#78716c'; // Default gray
  };

  useEffect(() => {
    if (!mapRef.current) return;

    if (!mapInstance.current) {
      mapInstance.current = L.map(mapRef.current).setView([39.0, 35.0], 6);

      L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; OpenStreetMap &copy; CARTO',
        subdomains: 'abcd',
        maxZoom: 19
      }).addTo(mapInstance.current);

      layersRef.current = L.layerGroup().addTo(mapInstance.current);
    }

    // Clear previous markers
    layersRef.current?.clearLayers();

    // 1. Add Existing Sites
    if (showExisting) {
      sites.forEach(site => {
        const matchesPeriod = !highlightedPeriod || site.period === highlightedPeriod;
        
        const opacity = highlightedPeriod && !matchesPeriod ? 0.3 : 1;
        // Increased emphasis for highlighted period (radius 15 for better visibility)
        const radius = matchesPeriod && highlightedPeriod ? 15 : 7; 
        const weight = matchesPeriod && highlightedPeriod ? 4 : 2;
        const fillColor = getColorForPeriod(site.period);
        const strokeColor = matchesPeriod && highlightedPeriod ? '#f59e0b' : '#fff'; // Amber border for highlight

        const marker = L.circleMarker([site.lat, site.lng], {
          radius: radius,
          fillColor: fillColor,
          color: strokeColor,
          weight: weight,
          opacity: opacity,
          fillOpacity: opacity * 0.9,
          className: matchesPeriod ? 'marker-existing-pulse' : ''
        });

        if (matchesPeriod && highlightedPeriod) {
          setTimeout(() => marker.bringToFront(), 0);
        }

        // Add Hover Effects for Existing Sites
        marker.on('mouseover', (e) => {
          e.target.bringToFront();
          if (marker._path) {
            marker._path.classList.add('marker-existing-hover');
          }
        });
        marker.on('mouseout', () => {
          if (marker._path) {
            marker._path.classList.remove('marker-existing-hover');
          }
        });

        marker.on('click', () => {
          onSiteClick(site, false);
        });
        
        // Rich Tooltip for Existing Sites
        marker.bindTooltip(`
          <div class="font-sans text-left min-w-[240px] overflow-hidden rounded-xl shadow-2xl border border-stone-100">
            <div class="bg-stone-50 border-b border-stone-200 p-3 flex justify-between items-start">
              <div class="flex-1 pr-2">
                <h3 class="font-extrabold text-sm text-stone-800 leading-tight">${site.name}</h3>
                <div class="text-[10px] text-stone-500 mt-0.5 flex items-center gap-1">
                   <span class="opacity-70">📍</span> ${site.location}
                </div>
              </div>
              <span class="text-[10px] font-bold text-white px-2 py-1 rounded-md shadow-sm whitespace-nowrap" style="background-color: ${fillColor}">${site.period}</span>
            </div>
            <div class="p-3 bg-white">
               ${site.description ? `
                <div class="text-xs text-stone-600 leading-relaxed italic border-l-2 border-stone-200 pl-2 bg-stone-50/50 py-1 rounded-r">
                  "${site.description.substring(0, 120)}${site.description.length > 120 ? '...' : ''}"
                </div>
               ` : '<span class="text-xs text-stone-400 italic">Açıklama bulunmuyor.</span>'}
               <div class="mt-2 flex justify-end">
                  <span class="text-[9px] font-bold text-amber-600 uppercase tracking-widest hover:underline cursor-pointer">Detayları İncele →</span>
               </div>
            </div>
          </div>
        `, { 
          direction: 'top', 
          offset: [0, -10], 
          opacity: 1, 
          className: 'custom-tooltip-modern shadow-xl !border-0 !p-0' 
        });

        layersRef.current?.addLayer(marker);
      });
    }

    // 2. Add Predicted Sites
    if (showPredicted) {
      predictedSites.forEach(site => {
        const matchesPeriod = !highlightedPeriod || (site.period && site.period === highlightedPeriod);
        
        if (highlightedPeriod && !matchesPeriod) return;

        const marker = L.circleMarker([site.lat, site.lng], {
          radius: 12,
          fillColor: "#e11d48", // Rose-600
          color: "#fff",
          weight: 3,
          opacity: 1,
          fillOpacity: 0.9,
          dashArray: "4, 4", 
          className: 'marker-predicted-pulse'
        });

        // Add distinct hover effect via class toggling
        marker.on('mouseover', (e) => {
          e.target.bringToFront();
          if (marker._path) {
            marker._path.classList.add('marker-predicted-hover');
          }
        });
        marker.on('mouseout', () => {
          if (marker._path) {
            marker._path.classList.remove('marker-predicted-hover');
          }
        });

        marker.on('click', () => {
          onSiteClick(site, true);
        });
        
        // Rich Tooltip for Predicted Sites with Magic Border Animation AND Background Pulse
        marker.bindTooltip(`
          <div class="font-sans text-left min-w-[300px] overflow-hidden rounded-xl shadow-2xl border-0 bg-white ring-1 ring-black/5">
            <!-- Header -->
            <div class="bg-gradient-to-br from-rose-600 via-rose-500 to-rose-600 p-4 relative overflow-hidden">
               <div class="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-20 mix-blend-overlay"></div>
               <div class="absolute -right-6 -top-6 w-24 h-24 bg-white/10 rounded-full blur-2xl"></div>
               
               <div class="flex items-start justify-between relative z-10 gap-3">
                  <div class="flex flex-col">
                    <span class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-black/20 text-[9px] font-bold text-white uppercase tracking-widest backdrop-blur-sm border border-white/10 w-fit mb-1.5">
                      <span class="w-1.5 h-1.5 bg-rose-200 rounded-full animate-pulse shadow-[0_0_5px_rgba(255,255,255,0.8)]"></span>
                      Yapay Zeka Tahmini
                    </span>
                    <span class="font-extrabold text-white text-lg leading-tight drop-shadow-md">${site.name}</span>
                  </div>
                  <div class="bg-white/20 backdrop-blur-md p-2 rounded-xl border border-white/40 shadow-lg shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="drop-shadow-sm"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>
                  </div>
               </div>
            </div>
            
            <!-- Body -->
            <div class="p-4 bg-white relative">
              <div class="flex items-center gap-2 mb-3">
                <span class="px-2.5 py-1 rounded-md text-[10px] font-bold text-rose-700 bg-rose-50 border border-rose-100 shadow-sm uppercase tracking-wide">
                  ${site.period || 'Dönem Belirsiz'}
                </span> 
              </div>
              
              <!-- Enhanced Magic Reason Box with Background Pulse -->
              <div class="relative z-0 rounded-xl overflow-hidden p-[2px] mb-3">
                 <!-- Animated Gradient Border Background -->
                 <div class="absolute inset-0 bg-gradient-to-r from-rose-400 via-amber-400 to-rose-400 animate-border-flow"></div>
                 
                 <!-- Inner Content with Strong Pulse Animation -->
                 <div class="relative z-10 bg-white rounded-[10px] p-3 h-full animate-reason-pulse">
                    <div class="flex items-center gap-2 mb-1.5 pb-1.5 border-b border-rose-100/50">
                        <span class="relative flex h-2 w-2">
                          <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                          <span class="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                        </span>
                        <span class="text-[9px] font-bold text-amber-600 uppercase tracking-widest">Neden Burası?</span>
                    </div>
                    <p class="text-xs text-stone-700 leading-relaxed italic font-medium">
                       "${site.reason.substring(0, 160)}${site.reason.length > 160 ? '...' : ''}"
                    </p>
                 </div>
              </div>

              <div class="mt-2 flex items-center justify-end gap-1.5 group/btn cursor-pointer">
                <span class="text-[10px] font-bold text-rose-500 uppercase tracking-widest group-hover/btn:text-rose-700 transition-colors">Detayları İncele</span>
                <div class="w-5 h-5 rounded-full bg-rose-50 flex items-center justify-center group-hover/btn:bg-rose-100 transition-colors">
                   <svg class="w-3 h-3 text-rose-600 group-hover/btn:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" /></svg>
                </div>
              </div>
            </div>
          </div>
        `, { 
          direction: 'top', 
          offset: [0, -20], 
          className: 'custom-tooltip-modern shadow-2xl !border-0 !p-0 !bg-transparent' 
        });

        layersRef.current?.addLayer(marker);
      });
    }

  }, [sites, predictedSites, onSiteClick, highlightedPeriod, showExisting, showPredicted]);

  // Sort periods by frequency for the legend
  const sortedPeriods = React.useMemo(() => {
    const counts: Record<string, number> = {};
    sites.forEach(s => {
      const p = s.period || 'Bilinmiyor';
      counts[p] = (counts[p] || 0) + 1;
    });
    return Object.entries(PERIOD_COLORS)
      .filter(([name]) => counts[name] > 0 || sites.some(s => s.period.includes(name))) 
      .sort((a, b) => (counts[b[0]] || 0) - (counts[a[0]] || 0));
  }, [sites]);

  return (
    <div className="relative w-full h-[600px] rounded-2xl overflow-hidden shadow-md border border-stone-200 z-0 group bg-stone-100">
      <div ref={mapRef} className="w-full h-full" />
      
      {/* Modernized Layer Controls */}
      <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-3">
        <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg border border-stone-200 p-2 flex flex-col gap-1.5 min-w-[160px]">
           <div className="text-[10px] uppercase font-bold text-stone-400 px-2 pb-1 border-b border-stone-100 mb-0.5 tracking-wider">Görünüm Ayarları</div>
          <button 
            onClick={() => setShowExisting(!showExisting)}
            className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-xs font-bold transition-all w-full group ${showExisting ? 'bg-amber-50 text-amber-800 border border-amber-100' : 'text-stone-400 hover:bg-stone-50 border border-transparent'}`}
          >
             <div className="flex items-center gap-2">
                {showExisting ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5 opacity-50" />}
                Mevcut
             </div>
             <span className={`text-[10px] min-w-[20px] h-5 flex items-center justify-center rounded-full transition-colors ${showExisting ? 'bg-amber-500 text-white shadow-sm' : 'bg-stone-200 text-stone-500'}`}>
               {existingCount}
             </span>
          </button>
          <button 
             onClick={() => setShowPredicted(!showPredicted)}
             className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-xs font-bold transition-all w-full group ${showPredicted ? 'bg-rose-50 text-rose-800 border border-rose-100' : 'text-stone-400 hover:bg-stone-50 border border-transparent'}`}
          >
             <div className="flex items-center gap-2">
                {showPredicted ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5 opacity-50" />}
                Tahminler
             </div>
             <span className={`text-[10px] min-w-[20px] h-5 flex items-center justify-center rounded-full transition-colors ${showPredicted ? 'bg-rose-500 text-white shadow-sm' : 'bg-stone-200 text-stone-500'}`}>
               {predictedCount}
             </span>
          </button>
        </div>
      </div>

      {/* Interactive Legend Overlay */}
      <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-stone-200 z-[1000] flex flex-col w-56 max-h-[300px] overflow-hidden transition-all duration-300">
        <div className="p-3 border-b border-stone-100 bg-stone-50/90 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-2 text-stone-700 font-bold text-xs uppercase tracking-wide">
            <Layers className="w-3.5 h-3.5 text-amber-600" />
            Dönem Filtresi
          </div>
          {highlightedPeriod && (
             <button 
               onClick={(e) => {
                 e.stopPropagation();
                 onPeriodSelect && onPeriodSelect(null);
               }}
               className="text-[9px] text-rose-600 bg-rose-50 hover:bg-rose-100 font-bold px-2 py-0.5 rounded transition-colors border border-rose-100"
             >
               TEMİZLE
             </button>
          )}
        </div>
        
        <div className="overflow-y-auto p-2 custom-scrollbar space-y-1 bg-white">
          {/* Prediction Legend Item */}
          {predictedSites.length > 0 && showPredicted && (
             <div className="flex items-center gap-3 p-2 rounded-lg bg-gradient-to-r from-rose-50 to-white border border-rose-100 mb-2">
               <div className="relative flex items-center justify-center w-4 h-4">
                 <div className="w-4 h-4 rounded-full border-2 border-rose-500 border-dashed absolute animate-spin-slow"></div>
                 <div className="w-2 h-2 rounded-full bg-rose-600"></div>
               </div>
               <span className="text-rose-800 text-xs font-bold">Yapay Zeka Tahmini</span>
             </div>
          )}
          
          <div className="text-[9px] font-bold text-stone-400 px-2 py-1 uppercase tracking-wider">Tarihi Dönemler</div>

          {/* Periods */}
          {sortedPeriods.map(([name, color]) => {
             const isActive = highlightedPeriod === name;
             const opacityClass = highlightedPeriod && !isActive ? 'opacity-40 grayscale' : 'opacity-100';

             return (
              <button
                key={name}
                onClick={() => onPeriodSelect && onPeriodSelect(isActive ? null : name)}
                className={`w-full flex items-center gap-3 p-2 rounded-lg transition-all group ${
                  isActive 
                    ? 'bg-amber-50 shadow-sm ring-1 ring-amber-200 translate-x-1' 
                    : 'hover:bg-stone-50'
                } ${opacityClass}`}
              >
                <div 
                  className={`w-3 h-3 rounded-full border border-black/10 shadow-sm transition-transform duration-300 ${isActive ? 'scale-125 ring-2 ring-offset-1 ring-amber-300' : 'group-hover:scale-110'}`} 
                  style={{ backgroundColor: color }}
                ></div>
                <span className={`text-xs text-left flex-1 ${isActive ? 'text-stone-900 font-bold' : 'text-stone-600 font-medium'}`}>
                  {name}
                </span>
                {isActive && <div className="w-1.5 h-1.5 rounded-full bg-amber-500 shadow-sm"></div>}
              </button>
             );
          })}
        </div>
      </div>
      
      {/* CSS for Animations */}
      <style>{`
        .custom-tooltip-modern {
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
        }
        .custom-tooltip-modern::before {
          display: none !important;
        }

        /* Breathing Pulse for Existing */
        @keyframes pulse-breathing {
          0% { transform: scale(1); opacity: 0.9; }
          50% { transform: scale(1.1); opacity: 1; stroke-width: 3px; }
          100% { transform: scale(1); opacity: 0.9; }
        }
        .marker-existing-pulse {
          animation: pulse-breathing 2.5s ease-in-out infinite;
          transform-origin: center center;
        }

        /* Existing Site Hover Effect */
        .marker-existing-hover {
          stroke: #d97706 !important; /* amber-600 */
          stroke-width: 4px !important;
          fill-opacity: 1 !important;
          transform: scale(1.2);
          transform-origin: center;
          transform-box: fill-box;
          transition: all 0.3s ease;
        }
        
        /* Dash Rotation for Predicted */
        @keyframes dash-rotate {
          0% { stroke-dashoffset: 0; stroke-width: 2px; }
          50% { stroke-dashoffset: 20; stroke-width: 4px; stroke: #fb7185; } 
          100% { stroke-dashoffset: 40; stroke-width: 2px; }
        }
        .marker-predicted-pulse {
           animation: dash-rotate 4s linear infinite;
           transition: all 0.3s ease;
        }

        /* SUPERNOVA EFFECT: Pronounced hover effect for predicted sites */
        @keyframes supernova-pulse {
          0% { 
            transform: scale(1.2); 
            box-shadow: 0 0 0 0 rgba(225, 29, 72, 0.7); 
            stroke: white; 
            stroke-width: 3px;
          }
          50% { 
            transform: scale(2.0); /* Larger expansion */
            stroke: #fbbf24; 
            stroke-width: 6px; 
            filter: drop-shadow(0 0 15px rgba(251, 191, 36, 0.8)); 
          }
          100% { 
            transform: scale(1.2); 
            box-shadow: 0 0 0 15px rgba(225, 29, 72, 0); 
            stroke: white; 
            stroke-width: 3px; 
          }
        }

        .marker-predicted-hover {
           stroke-dasharray: 0 !important;
           fill-opacity: 1 !important;
           animation: supernova-pulse 1.5s infinite ease-in-out !important;
           transform-origin: center;
           transform-box: fill-box;
           z-index: 9999;
        }

        .animate-spin-slow {
          animation: spin 6s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Magic Border Flow Animation */
        @keyframes border-flow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-border-flow {
            background-size: 200% 200%;
            animation: border-flow 3s ease infinite;
        }

        /* Background Pulse Animation for Reason Box (More Visible) */
        @keyframes reason-bg-pulse {
          0% { background-color: #ffffff; box-shadow: inset 0 0 0 0 rgba(251, 191, 36, 0); }
          50% { background-color: #fff1f2; box-shadow: inset 0 0 10px 0 rgba(244, 63, 94, 0.1); } /* rose-50ish */
          100% { background-color: #ffffff; box-shadow: inset 0 0 0 0 rgba(251, 191, 36, 0); }
        }
        .animate-reason-pulse {
          animation: reason-bg-pulse 2.5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}